import { useState } from "react";
import { TimeSeriesChart } from "../../../components/graphs/TimeSeriesChart";
import { GrainSelector, MetricSelector } from "../../../components/graphs/ChartControls";
import { CATEGORICAL } from "../../../config/vizPalette";
import { Card, InsightSection, QueryState } from "./InsightSection";
import { useCompletionSeries, useInsightMetrics } from "./useSteamInsights";

const percent = (value) => (value == null ? "–" : `${value.toFixed(1)}%`);
const count = (value) => (value == null ? "–" : Math.round(value).toLocaleString("en-GB"));

const START_SOURCE_LABELS = {
    achievement_unlock: "an achievement unlock",
    last_played: "the last-played date",
    refresh_detected: "the daily refresh",
};

const StartSources = ({ meta }) => {
    const sources = Object.entries(meta?.start_sources || {}).filter(([, n]) => n > 0);
    if (!sources.length) return null;
    return (
        <p className="mt-4 text-sm text-gray-500">
            Start dates come from{" "}
            {sources
                .map(([source, n]) => `${START_SOURCE_LABELS[source] || source} for ${n.toLocaleString("en-GB")} games`)
                .join(", ")}
            .
            {meta.played_without_date > 0 &&
                (meta.played_without_date === 1
                    ? " 1 played game has no dated evidence yet, so it isn't on the timeline."
                    : ` ${meta.played_without_date} played games have no dated evidence yet, so they aren't on the timeline.`)}
        </p>
    );
};

export const CompletionOverTimeChart = () => {
    const registry = useInsightMetrics();
    const [metric, setMetric] = useState(null);
    const [grain, setGrain] = useState(null);

    const activeMetric = metric || registry.data?.default_metric;
    const activeGrain = grain || registry.data?.default_grain;
    const query = useCompletionSeries(activeMetric, activeGrain);
    const definition = registry.data?.metrics.find((m) => m.key === activeMetric);

    return (
        <InsightSection
            id="completion-over-time"
            title="Completion over time"
            intro="Your achievement completion at the end of each period, rebuilt from unlock dates. A game only joins once you've started it, so unplayed purchases never pull it down."
        >
            <Card>
                {registry.data && (
                    <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                        <MetricSelector metrics={registry.data.metrics} value={activeMetric} onChange={setMetric} />
                        <GrainSelector grains={registry.data.grains} value={activeGrain} onChange={setGrain} />
                    </div>
                )}
                <QueryState
                    query={registry.isLoading ? registry : query}
                    isEmpty={!query.data?.series?.length}
                    emptyText="No dated unlocks yet."
                >
                    <TimeSeriesChart
                        data={query.data?.series || []}
                        grain={activeGrain}
                        series={[
                            {
                                key: "value",
                                label: definition?.label || "Completion",
                                type: "line",
                                axis: "left",
                                colour: CATEGORICAL[0],
                                format: percent,
                            },
                            {
                                key: "games",
                                label: definition?.count_label || "Games",
                                type: "line",
                                axis: "right",
                                colour: CATEGORICAL[1],
                                format: count,
                            },
                        ]}
                        yLeft={{ label: "Completion", domain: [0, 100], format: (v) => `${v}%` }}
                        yRight={{ label: "Games", format: count }}
                        annotations={[
                            { date: query.data?.meta?.tracking_started_utc, label: "Daily refresh began" },
                        ]}
                        ariaLabel={`${definition?.label || "Completion"} by ${activeGrain}`}
                    />
                    <StartSources meta={query.data?.meta} />
                </QueryState>
            </Card>
        </InsightSection>
    );
};
